import { AfterContentInit, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { Advertiser, CampaignsAdvertiserAdminApiClient, CoinReward, ProductReward, PunchCardStrategyType, PunchCardStrategy } from 'src/app/api/api-campaigns.service.generated';
import { RewardsProductApiClient } from 'src/app/api/api-rewards.service.generated';
import { AdvertiserCampaignRewardEditComponent } from '../../advertiser-campaign-reward-edit/advertiser-campaign-reward-edit.component';
import { PunchCardCampaignService } from '../../punch-card-campaign-edit/punch-card-campaign-edit.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PhotoComponent, ButtonComponent } from 'wecheer-common';
import { lastValueFrom } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-stamp-setting',
  templateUrl: './stamp-setting.component.html',
  styleUrls: ['./stamp-setting.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FlexLayoutModule,
    PhotoComponent,
    ButtonComponent
]
})
export class StampSettingComponent implements OnInit, OnChanges, AfterContentInit {
  private dialog = inject(MatDialog);
  private advertiserApi = inject(CampaignsAdvertiserAdminApiClient);
  punchCardCampaignService = inject(PunchCardCampaignService);
  private rewardProductsApi = inject(RewardsProductApiClient);

  @Input() strategy: PunchCardStrategy;
  @Input() strategyType: PunchCardStrategyType | any | undefined;
  @Input() allowFlow: boolean;
  @Input() advertiserId: string;
  @Input() campaignId: string;
  @Input() isCloning: boolean = false;

  isLoading = false;
  advertiser: Advertiser;
  rewardProductsOptions: ProductReward[] = [];
  uploadKeyReward: string; // in case new reward was created we store the temp upload key (to be moved to advertiser folder)


  form = new FormGroup({
    rewardTitle: new FormControl(''),
    rewardDescription: new FormControl(''),
    maxStampCount: new FormControl(null, [Validators.required, Validators.min(1), Validators.pattern('([1-9]|[12][0-9]|9[00])')]),  // [0:90]
    productReward: new FormControl(null, [Validators.required]),

    restartLimit: new FormControl()
  });

  async ngAfterContentInit() {
    if (!this.advertiserId) return;

    const productReward = await this.getProductReward();
    this.setFormValue(this.strategy, productReward);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!this.advertiserId) return;

    if (changes['strategy'] && this.strategy) {
      const productReward = await this.getProductReward();
      this.setFormValue(this.strategy, productReward);
    }
  }

  ngOnInit(): void {

    this.getAdvertiser();
    this.getProductRewards();

    this.punchCardCampaignService.onGetProductFinished
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.getAdvertiser();
        this.getProductRewards();
      });
  }

  isValid() {
    if (!this.form.valid) {
      this.forceValidatorsToDisplayErrors();
    }

    return this.form.valid;
  }

  forceValidatorsToDisplayErrors() {
    this.form.markAllAsTouched();
  }

  setFormValue(model: PunchCardStrategy, productReward: ProductReward) {
    if (!model) return;

    this.form.patchValue({
      rewardTitle: model.rewardTitle || (productReward ? productReward.rewardTitle : ''),
      rewardDescription: model.rewardDescription || (productReward ? productReward.rewardDescription : ''),
      maxStampCount: model.maxStampCount,
      productReward: productReward,
      restartLimit: model.restartLimit
    });
  }

  getFormValue() {
    if (!this.form.valid) {
      return undefined;
    }
    const f = this.form.value;
    let model: PunchCardStrategy = new PunchCardStrategy({});
    model.restartLimit = f.restartLimit;
    model.maxStampCount = f.maxStampCount;
    if (f.productReward) {
      const pr = f.productReward as ProductReward;
      model.productSlug = pr.productSlug;
      model.rewardTitle = pr.rewardTitle;
      model.rewardDescription = pr.rewardDescription;
      model.rewardPhotoUrl = pr.rewardPhotoUrl;
      model.rewardThumbUrl = pr.rewardThumbUrl;
    }
    return model;
  }

  async forceRefresh() {
    if (this.strategy) {
      const productReward = await this.getProductReward();
      this.setFormValue(this.strategy, productReward);
    }
  }

  async getProductReward() {
    if (!this.campaignId && !this.isCloning) return undefined;

    const isAdding = this.campaignId === 'new';
    if (isAdding) return undefined;

    if (!this.strategy) return undefined;
    else if (!isAdding && this.strategy.strategyType == PunchCardStrategyType.Points) return undefined;

    if (!!this.strategy.flow && !this.isCloning) return undefined;     // if multi-tier flow is set -> will use complete reward from flow.steps

    let slug = (this.strategy && this.strategy.productSlug) || undefined;

    if (!slug) {
      slug = !isAdding && this.campaignId
        ? `reward_${this.campaignId.split('@')[1]}`
        : undefined;
    }

    const productInfo = slug
      ? await lastValueFrom(this.rewardProductsApi.getProductBySlug(slug))
      : undefined;

    const productReward = productInfo
      ? new ProductReward({
        productSlug: productInfo.slug,
        rewardTitle: productInfo.name,
        rewardDescription: productInfo.description,
        rewardThumbUrl: productInfo.photoUrl,
        rewardPhotoUrl: productInfo.photoUrl
      })
      : undefined;

    return productReward;
  }


  async getAdvertiser() {
    this.advertiser = await lastValueFrom(this.advertiserApi.getAdvertiserById(this.advertiserId));
  }

  async getProductRewards() {
    this.rewardProductsOptions = this.punchCardCampaignService.rewardProductsOptions;
  }

  onRewardSelected(productReward: ProductReward) {
    this.form.patchValue({
      rewardTitle: (productReward && productReward.rewardTitle) || '',
      rewardDescription: (productReward && productReward.rewardDescription) || ''
    });
  }

  // product reward add
  async onAddRewardProduct() {
    const productReward = await AdvertiserCampaignRewardEditComponent.showDialog(this.dialog, { advertiser: this.advertiser });
    if (!productReward) return;

    this.rewardProductsOptions = [
      productReward,                    // push at 0 index
      ...this.rewardProductsOptions
    ];

    this.uploadKeyReward = productReward.uploadKey;  // this flag must create the product reward the the campaing is created

    this.form.patchValue({
      productReward: productReward,
      rewardTitle: (productReward && productReward.rewardTitle) || '',
      rewardDescription: (productReward && productReward.rewardDescription) || ''
    });
  }

  async onRemoveRewardProduct() {
    this.form.patchValue({ productReward: undefined });
  }

  compareFn: ((a: any, b: any) => boolean) | null = (a: any, b: any) => a && b && a.id === b.id;
  compareRewardFn: ((a: CoinReward, b: CoinReward) => boolean) | null = (a: any, b: any) => a && b && a.coins === b.coins;
  compareProductRewardFn: ((a: ProductReward, b: ProductReward) => boolean) | null = (a: ProductReward, b: ProductReward) => a && b && a.productSlug === b.productSlug;

}
